import {
  Alert,
  Body,
  Button,
  colors,
  darkThemeSelector,
  Heading,
  Link,
  Pane,
  PaneContent,
  PaneHeader,
  sizing,
  styled,
  Text,
  VStack,
} from '@meterup/atto';
import { useState } from 'react';

import type { ErrorFallbackProps } from './utils';
import ResetErrorOnNavigation from './ResetErrorOnNavigation';

const DevelopmentErrorFallbackCode = styled(Body, {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  padding: sizing.squish,
  overflow: 'auto',
  borderRadius: '$10',
  background: colors.bgNeutralLight,
  strokeAll: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
    strokeAll: colors.strokeNeutralDark,
  },
});

const DevelopmentErrorFallbackContainer = styled('div', {
  display: 'grid',
  gridTemplateAreas: "'pane'",
  background: colors.bgApplicationLight,
  widthAll: '100%',
  heightAll: '100%',

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
  },

  variants: {
    frame: {
      drawer: {
        gridArea: 'drawer-content',
      },
      dialog: {
        gridArea: 'dialog-content',
      },
      pane: {
        gridArea: 'pane',
      },
    },
  },
});

export function DevelopmentErrorFallback({
  error,
  componentStack,
  resetError,
  frame = 'pane',
}: ErrorFallbackProps) {
  const [currentStack, setCurrentStack] = useState<'error' | 'component'>('error');
  return (
    <DevelopmentErrorFallbackContainer frame={frame}>
      <Pane layoutMode="detailed" contentMode="stretch" widthMode="centered" preset="intermediate">
        <ResetErrorOnNavigation resetError={resetError} />
        <PaneHeader
          heading={
            <VStack spacing={8}>
              <Text>
                <Heading family="monospace" variant="negative">
                  Uncaught error:
                </Heading>{' '}
                <Heading family="monospace" variant="neutral" whitespace="pre-wrap">
                  {error.name}
                </Heading>
              </Text>
              <Text>
                <Body weight="extra-bold">Message:</Body>{' '}
                <Body family="monospace" whitespace="pre-wrap">
                  {error.message}
                </Body>
              </Text>
            </VStack>
          }
          actions={
            <Button
              type="button"
              onClick={resetError}
              arrangement="leading-icon"
              icon="reset"
              size="medium"
              variant="secondary"
            >
              Reset
            </Button>
          }
          tabs={[
            {
              selected: currentStack === 'error',
              onClick: () => setCurrentStack('error'),
              label: 'Stack trace',
            },
            {
              selected: currentStack === 'component',
              onClick: () => setCurrentStack('component'),
              label: 'Component stack',
            },
          ]}
        />
        <PaneContent gutter="all" spacing={8}>
          <Alert
            variant="neutral"
            icon="information"
            copy={
              <Text>
                This error would be logged to{' '}
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://meter-inc.sentry.io/issues/"
                >
                  Sentry
                </Link>{' '}
                in staging or production.
              </Text>
            }
          />
          {currentStack === 'error' ? (
            <DevelopmentErrorFallbackCode family="monospace" whitespace="pre-wrap">
              {error.stack}
            </DevelopmentErrorFallbackCode>
          ) : (
            <DevelopmentErrorFallbackCode family="monospace" whitespace="pre-wrap">
              {componentStack}
            </DevelopmentErrorFallbackCode>
          )}
        </PaneContent>
      </Pane>
    </DevelopmentErrorFallbackContainer>
  );
}
